import {
  INSURANCE_FORM_STEPS,
  INSURANCE_PROVIDERS,
  NEW_YORK_STATE,
} from "./constants";
import insuranceFlows from "./insuranceFlows.json";

// ============================================================================
// Types
// ============================================================================

interface NavigationContext {
  planType?: string;
  planCoverage?: string;
  bcbsProvider?: string;
  patientState?: string;
  isSecondary?: boolean;
}

interface Condition {
  planType?: string;
  planCoverage?: string;
  bcbsProvider?: string;
  outsideNY?: boolean;
  isSecondary?: boolean;
}

interface ProviderConfig {
  displayName: string;
  planTypes?: string[];
  steps: string[];
  planCoverageOptions?: string[] | Record<string, string[]>; // Can be array or object by plan type
  conditions?: Record<string, Condition[]>;
  planOptions?: Record<string, Array<{ label: string; value: string }>>;
  bcbsProviders?:
    | Array<{ label: string; value: string }>
    | Record<
        string,
        | Array<{ label: string; value: string }>
        | Record<string, Array<{ label: string; value: string }>>
      >;
  skipProvider?: boolean;
}

type ProvidersConfig = Record<string, ProviderConfig>;

// ============================================================================
// Helpers
// ============================================================================

const NY_STATES = [NEW_YORK_STATE, "NY"];

function isOutsideNY(state?: string): boolean {
  return !state || !NY_STATES.includes(state);
}

function matchesCondition(
  condition: Condition,
  ctx: NavigationContext,
): boolean {
  if (condition.planType !== undefined && condition.planType !== ctx.planType) {
    return false;
  }
  if (
    condition.planCoverage !== undefined &&
    condition.planCoverage !== ctx.planCoverage
  ) {
    return false;
  }
  if (
    condition.bcbsProvider !== undefined &&
    condition.bcbsProvider !== ctx.bcbsProvider
  ) {
    return false;
  }
  if (
    condition.outsideNY !== undefined &&
    condition.outsideNY !== isOutsideNY(ctx.patientState)
  ) {
    return false;
  }
  if (
    condition.isSecondary !== undefined &&
    condition.isSecondary !== Boolean(ctx.isSecondary)
  ) {
    return false;
  }
  return true;
}

function shouldShowStep(
  step: string,
  config: ProviderConfig,
  ctx: NavigationContext,
): boolean {
  // Special case: PLAN_COVERAGE step - check if options exist for this plan type
  if (step === "PLAN_COVERAGE" && config.planCoverageOptions) {
    if (!Array.isArray(config.planCoverageOptions)) {
      // Conditional options - check if plan type has options
      const planType = ctx.planType;
      if (planType && !config.planCoverageOptions[planType]) {
        return false; // No options for this plan type, skip step
      }
    }
  }

  const conditions = config.conditions?.[step];

  // No conditions means always show
  if (!conditions || conditions.length === 0) {
    return true;
  }

  // Show if ANY condition matches
  return conditions.some((cond) => matchesCondition(cond, ctx));
}

function stepToEnum(step: string): INSURANCE_FORM_STEPS {
  const mapping: Record<string, INSURANCE_FORM_STEPS> = {
    PROVIDER: INSURANCE_FORM_STEPS.PROVIDER,
    OTHER_INSURANCE: INSURANCE_FORM_STEPS.OTHER_INSURANCE,
    PLAN_TYPE: INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER,
    PLAN_COVERAGE: INSURANCE_FORM_STEPS.PLAN_COVERAGE,
    MEDICAL_CONDITIONS: INSURANCE_FORM_STEPS.MEDICAL_CONDITIONS,
    BCBS_PLAN: INSURANCE_FORM_STEPS.BCBS_PLAN,
    ANTHEM_EMPIRE_POS: INSURANCE_FORM_STEPS.ANTHEM_EMPIRE_POS,
    UHC_PLAN: INSURANCE_FORM_STEPS.UHC_PLAN,
    AETNA_PLAN: INSURANCE_FORM_STEPS.AETNA_PLAN,
    FIDELIS_PLAN: INSURANCE_FORM_STEPS.FIDELIS_PLAN,
    MEMBER_ID: INSURANCE_FORM_STEPS.MEMBER_ID_FORM,
  };
  return mapping[step] || INSURANCE_FORM_STEPS.MEMBER_ID_FORM;
}

function enumToStep(formStep: INSURANCE_FORM_STEPS): string {
  const mapping: Record<INSURANCE_FORM_STEPS, string> = {
    [INSURANCE_FORM_STEPS.PROVIDER]: "PROVIDER",
    [INSURANCE_FORM_STEPS.OTHER_INSURANCE]: "OTHER_INSURANCE",
    [INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER]: "PLAN_TYPE",
    [INSURANCE_FORM_STEPS.PLAN_COVERAGE]: "PLAN_COVERAGE",
    [INSURANCE_FORM_STEPS.MEDICAL_CONDITIONS]: "MEDICAL_CONDITIONS",
    [INSURANCE_FORM_STEPS.BCBS_PLAN]: "BCBS_PLAN",
    [INSURANCE_FORM_STEPS.ANTHEM_EMPIRE_POS]: "ANTHEM_EMPIRE_POS",
    [INSURANCE_FORM_STEPS.UHC_PLAN]: "UHC_PLAN",
    [INSURANCE_FORM_STEPS.AETNA_PLAN]: "AETNA_PLAN",
    [INSURANCE_FORM_STEPS.FIDELIS_PLAN]: "FIDELIS_PLAN",
    [INSURANCE_FORM_STEPS.MEMBER_ID_FORM]: "MEMBER_ID",
  };
  return mapping[formStep] || "MEMBER_ID";
}

function getProviderKey(providerName?: string): string | null {
  if (!providerName) return null;

  const providers = insuranceFlows.providers as ProvidersConfig;
  const normalizedInput = providerName.toLowerCase().replace(/[\s_-]/g, "");

  // Direct key match first
  if (providers[providerName]) {
    return providerName;
  }

  // Try to find by normalized key or displayName
  for (const key of Object.keys(providers)) {
    const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, "");
    const normalizedDisplayName = providers[key].displayName
      .toLowerCase()
      .replace(/[\s_-]/g, "");

    if (
      normalizedKey === normalizedInput ||
      normalizedDisplayName === normalizedInput
    ) {
      return key;
    }
  }

  return null;
}

function getProviderConfig(providerName?: string): ProviderConfig | null {
  const key = getProviderKey(providerName);
  if (!key) return null;
  return (insuranceFlows.providers as ProvidersConfig)[key] || null;
}

function getVisibleSteps(
  config: ProviderConfig,
  ctx: NavigationContext,
): string[] {
  return config.steps.filter((step) => shouldShowStep(step, config, ctx));
}

// ============================================================================
// Navigation Functions
// ============================================================================

export const getNextInsuranceStep = (
  currentForm: INSURANCE_FORM_STEPS,
  response?: string,
  insuranceProvider?: string,
  planTypeProvider?: string,
  patientState?: string,
  planCoverage?: string,
  bcbsProvider?: string,
  isSecondary?: boolean,
): INSURANCE_FORM_STEPS | null => {
  // IMPORTANT: Use `response` for the value just selected on the current step
  // React state updates are async, so store values may be stale
  const effectiveProvider =
    currentForm === INSURANCE_FORM_STEPS.PROVIDER
      ? response
      : insuranceProvider;

  // Handle PROVIDER step specially - it's before we know the provider config
  if (currentForm === INSURANCE_FORM_STEPS.PROVIDER) {
    const config = getProviderConfig(effectiveProvider);
    if (config?.skipProvider) {
      // Traditional Medicare, Humana - go straight to their first step
      return stepToEnum(config.steps[0]);
    }
    // Check if "other" provider
    if (effectiveProvider === "other") {
      return INSURANCE_FORM_STEPS.OTHER_INSURANCE;
    }
    // If the provider has exactly one plan type, skip the PLAN_TYPE step
    if (config && config.planTypes && config.planTypes.length === 1) {
      const ctx: NavigationContext = {
        planType: config.planTypes[0],
        isSecondary,
      };
      const visibleSteps = getVisibleSteps(config, ctx);
      return stepToEnum(visibleSteps[0]);
    }

    // Most providers go to PLAN_TYPE
    return INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER;
  }

  const config = getProviderConfig(effectiveProvider);

  if (!config) {
    return INSURANCE_FORM_STEPS.MEMBER_ID_FORM;
  }

  const effectivePlanType =
    currentForm === INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER
      ? response
      : planTypeProvider;

  const effectivePlanCoverage =
    currentForm === INSURANCE_FORM_STEPS.PLAN_COVERAGE
      ? response
      : planCoverage;

  const effectiveBcbsProvider =
    currentForm === INSURANCE_FORM_STEPS.BCBS_PLAN ? response : bcbsProvider;

  const ctx: NavigationContext = {
    planType: effectivePlanType,
    planCoverage: effectivePlanCoverage,
    bcbsProvider: effectiveBcbsProvider,
    patientState,
    isSecondary,
  };

  const visibleSteps = getVisibleSteps(config, ctx);
  const currentStep = enumToStep(currentForm);
  const currentIndex = visibleSteps.indexOf(currentStep);

  if (currentIndex === -1) {
    // Current step not in flow - find where we should be
    // This handles edge cases where context changed
    if (currentForm === INSURANCE_FORM_STEPS.MEMBER_ID_FORM) {
      return null;
    }
    return INSURANCE_FORM_STEPS.MEMBER_ID_FORM;
  }

  if (currentIndex >= visibleSteps.length - 1) {
    // At the end of insurance flow
    return null;
  }

  if (!config) {
    return INSURANCE_FORM_STEPS.MEMBER_ID_FORM;
  }

  return stepToEnum(visibleSteps[currentIndex + 1]);
};

export const getPrevInsuranceStep = (
  currentForm: INSURANCE_FORM_STEPS,
  insuranceProvider: string,
  otherInsuranceProvider?: string | null,
  planTypeProvider?: string | null,
  patientState?: string,
  planCoverage?: string,
  bcbsProvider?: string,
  isSecondary?: boolean,
): INSURANCE_FORM_STEPS | null => {
  // Handle going back from PLAN_TYPE
  if (currentForm === INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER) {
    if (otherInsuranceProvider) {
      return INSURANCE_FORM_STEPS.OTHER_INSURANCE;
    }
    return INSURANCE_FORM_STEPS.PROVIDER;
  }

  // Handle going back from OTHER_INSURANCE
  if (currentForm === INSURANCE_FORM_STEPS.OTHER_INSURANCE) {
    return INSURANCE_FORM_STEPS.PROVIDER;
  }

  const config = getProviderConfig(insuranceProvider);
  if (!config) {
    return INSURANCE_FORM_STEPS.PROVIDER;
  }

  // For skipProvider providers (Traditional Medicare, etc.), going back from first step
  if (config.skipProvider && currentForm === stepToEnum(config.steps[0])) {
    return INSURANCE_FORM_STEPS.PROVIDER;
  }

  const ctx: NavigationContext = {
    planType: planTypeProvider || undefined,
    planCoverage,
    bcbsProvider,
    patientState,
    isSecondary,
  };

  const visibleSteps = getVisibleSteps(config, ctx);
  const currentStep = enumToStep(currentForm);
  const currentIndex = visibleSteps.indexOf(currentStep);

  if (currentIndex <= 0) {
    // At the start of the provider flow, go back to PLAN_TYPE or PROVIDER
    if (config.skipProvider) {
      return INSURANCE_FORM_STEPS.PROVIDER;
    }
    // Skip PLAN_TYPE if provider has only one plan type
    if (config.planTypes && config.planTypes.length === 1) {
      return INSURANCE_FORM_STEPS.PROVIDER;
    }
    return INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER;
  }

  return stepToEnum(visibleSteps[currentIndex - 1]);
};

// ============================================================================
// Config Accessors (for form components)
// ============================================================================

export function getAutoSelectedPlanType(providerName?: string): string | null {
  const config = getProviderConfig(providerName);
  if (config?.planTypes && config.planTypes.length === 1) {
    return config.planTypes[0];
  }
  return null;
}

export function getProviderPlanTypes(providerName?: string): string[] {
  const config = getProviderConfig(providerName);
  if (!config?.planTypes || config.planTypes.length === 0) {
    return insuranceFlows.defaults.planTypes;
  }
  return config.planTypes;
}

export function getPlanCoverageOptions(
  providerName?: string,
  planType?: string,
): string[] {
  const config = getProviderConfig(providerName);
  if (!config?.planCoverageOptions) {
    return insuranceFlows.defaults.planCoverageOptions;
  }

  // Handle conditional options by plan type (object format)
  if (!Array.isArray(config.planCoverageOptions)) {
    if (planType && config.planCoverageOptions[planType]) {
      return config.planCoverageOptions[planType];
    }
    // No matching plan type - return defaults
    return insuranceFlows.defaults.planCoverageOptions;
  }

  // Simple array format
  return config.planCoverageOptions;
}

export function getProviderPlanOptions(
  providerName?: string,
  planType?: string,
): Array<{ label: string; value: string }> | null {
  const config = getProviderConfig(providerName);
  if (!config?.planOptions || !planType) {
    return null;
  }
  return config.planOptions[planType] || null;
}

export function getBcbsProviders(
  planType?: string,
  planCoverage?: string,
): Array<{ label: string; value: string }> {
  const bcbsConfig = (insuranceFlows.providers as ProvidersConfig).bcbs;
  if (!bcbsConfig?.bcbsProviders) {
    return [];
  }

  const providers = bcbsConfig.bcbsProviders;

  // Check if it's a simple array (all options for all paths)
  if (Array.isArray(providers)) {
    return providers;
  }

  // Conditional by planType and/or planCoverage
  if (planType && typeof providers === "object") {
    const byPlanType = providers[planType];

    if (!byPlanType) {
      // No options for this plan type - return empty or default
      return [];
    }

    // Check if byPlanType is array (same for all coverages) or object (by coverage)
    if (Array.isArray(byPlanType)) {
      return byPlanType;
    }

    // Nested by planCoverage
    if (planCoverage && byPlanType[planCoverage]) {
      return byPlanType[planCoverage];
    }

    // Return first available if no coverage match
    const firstKey = Object.keys(byPlanType)[0];
    return firstKey ? byPlanType[firstKey] : [];
  }

  return [];
}

// ============================================================================
// Legacy helper exports (for backward compatibility)
// ============================================================================

export const isStandardCoverage = (coverage: string) => {
  return (
    coverage === INSURANCE_PROVIDERS.cdphp ||
    coverage === INSURANCE_PROVIDERS.mvp ||
    coverage === INSURANCE_PROVIDERS.univera
  );
};

export const isIndependentHealthCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.independentHealth;
};

export const isBcbsCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.bcbs;
};

export const isNonBcbsCoverage = (coverage: string) => {
  return (
    coverage === INSURANCE_PROVIDERS.cigna ||
    coverage === INSURANCE_PROVIDERS.other
  );
};

export const isUhcCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.uhc;
};

export const isTraditionalOrHumane = (coverage: string) => {
  return (
    coverage === INSURANCE_PROVIDERS.traditionalMedicare ||
    coverage === INSURANCE_PROVIDERS.humanaMedicare
  );
};

export const isAetnaCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.aetna;
};

export const isUpmcCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.upmc;
};

export const isTrinityMedicareCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.trinityMedicare;
};

export const isHarvardPilgrimCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.harvardPilgrim;
};

export const isFidelisCoverage = (coverage: string) => {
  return coverage === INSURANCE_PROVIDERS.fidelis;
};
